/**
 * Central Game Type Registry
 *
 * THE single source of truth for all supported game types.
 * Add a new game type here and every app picks it up automatically.
 */

import { type SportCode, SUPPORTED_SPORT_CODES } from './leagues';

/** Union of all supported game type codes */
export type GameTypeCode = 'pickem' | 'squares' | 'daily_reveal' | 'survivor' | 'bingo';

export interface GameTypeConfig {
  /** Lowercase game type identifier stored in DB, e.g. 'pickem' */
  code: GameTypeCode;
  /** Human-readable label, e.g. "Pick'em" */
  label: string;
  /** Admin panel route prefix, e.g. '/dashboard/pickems' */
  adminRoute: string;
  /** Icon name used in admin sidebar (from Icon component) */
  iconName: string;
  /** If set, these sports are excluded from this game type */
  excludedSports?: SportCode[];
}

export const GAME_TYPES: readonly GameTypeConfig[] = [
  { code: 'pickem', label: "Pick'em", adminRoute: '/dashboard/pickems', iconName: 'CrownSimple' },
  /*
   * MMA is out of the three team-shaped games: a bout's synthesised score is 1-0 or 0-0, so a
   * Squares board could only ever settle on 0s and 1s; Survivor picks a team to win each week;
   * and Daily Reveal's packs are built from team-sport prop markets MMA has none of.
   *
   * Tennis (DKPLAT-844) is Pick'em only for its first release, as NASCAR is: Squares needs a
   * points-shaped score, Survivor and Daily Reveal are built around team slates, and Bingo's
   * pool builder has not been checked against tennis stats.
   */
  { code: 'squares', label: 'Squares', adminRoute: '/dashboard/squares', iconName: 'GridFour', excludedSports: ['PGA', 'MMA', 'NASCAR', 'TENNIS'] },
  { code: 'daily_reveal', label: 'Daily Reveal', adminRoute: '/dashboard/daily-reveal', iconName: 'Lightning', excludedSports: ['PGA', 'MMA', 'NASCAR', 'TENNIS'] },
  { code: 'survivor', label: 'Survivor', adminRoute: '/dashboard/survivor', iconName: 'Trophy', excludedSports: ['PGA', 'MMA', 'NASCAR', 'TENNIS'] },
  // Bingo tiles are player props, which PGA has no equivalent of — same exclusion the other
  // prop-driven games carry (DKPLAT-566).
  { code: 'bingo', label: 'Bingo', adminRoute: '/dashboard/bingo', iconName: 'GridFour', excludedSports: ['PGA', 'NASCAR', 'TENNIS'] },
];

/** e.g. ['pickem', 'squares', 'daily_reveal'] */
export const GAME_TYPE_CODES: readonly GameTypeCode[] = GAME_TYPES.map(g => g.code);

/** e.g. { pickem: "Pick'em", squares: "Squares", daily_reveal: "Daily Reveal" } */
export const GAME_TYPE_LABELS: Record<GameTypeCode, string> = Object.fromEntries(
  GAME_TYPES.map(g => [g.code, g.label])
) as Record<GameTypeCode, string>;

/** Get the human-readable label for a game type code */
export function getGameTypeLabel(code: string): string {
  return GAME_TYPE_LABELS[code as GameTypeCode] ?? code;
}

/** Runtime validation helper */
export function isValidGameType(code: string): code is GameTypeCode {
  return (GAME_TYPE_CODES as readonly string[]).includes(code);
}

/** Get sports available for a game type */
export function getSportsForGameType(gameTypeCode: GameTypeCode): SportCode[] {
  const gameType = GAME_TYPES.find(g => g.code === gameTypeCode);
  if (!gameType) return [];
  if (gameType.excludedSports) {
    return (SUPPORTED_SPORT_CODES as unknown as SportCode[]).filter(
      s => !gameType.excludedSports!.includes(s)
    );
  }
  return [...SUPPORTED_SPORT_CODES] as SportCode[];
}

/** Check if a sport is available for a game type */
export function isSportAvailableForGameType(sport: string, gameTypeCode: GameTypeCode): boolean {
  return getSportsForGameType(gameTypeCode).includes(sport.toUpperCase() as SportCode);
}
